import React, { useEffect, useState } from 'react';
import Breadcrumbs, { IBreadcrumbsLink } from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import LEAccordions, { ELEAccordionsPageTypes } from 'components/organisms/LEAccordions';
import { useLocation, useNavigate } from 'react-router-dom';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { useTranslations } from 'hooks/useTranslations';
import { ESelectLE } from '../../Firearms/RegisteredFirearmsSearchForm';

export enum ESelectLegalEntityContext {
  FIREARMS = 'firearms',
  TRANSACTIONS = 'transactions',
  ANCILLARY_TRANSACTIONS = 'ancillaryTransactions',
  ANCILLARY_GROUP_TRANSACTIONS = 'ancillaryGroupTransactions',
  SEARCH_TRANSACTIONS = 'searchTransactions',
  SEARCH_ANCILLARIES = 'searchAncillaries',
  ASSOCIATED_INDIVIDUAL = 'associatedIndividual',
  SEARCH_LICENCES = 'searchLicences',
}

const SelectLegalEntityPage = () => {
  const { state: routerState }: any = useLocation();
  const [newBreadcrumbs, setNewBreadcrumbs] = useState<(string | IBreadcrumbsLink)[] | []>([]);
  const navigate = useNavigate();
  const { t } = useTranslations();
  let handleSelect: any = () => null;
  if (routerState?.context === ESelectLegalEntityContext.FIREARMS) {
    handleSelect = (params: any) => {
      let formValues = routerState?.form;
      if (params.type === ESelectLE.keeper) {
        formValues = {
          ...formValues,
          keeperId: params.legalEntity.id,
        };
      } else if (params.type === ESelectLE.owner) {
        formValues = {
          ...formValues,
          ownerId: params.legalEntity.id,
        };
      }
      if (routerState.returnPath) {
        navigate(routerState.returnPath, {
          state: {
            legalityUniqueId: routerState?.legalityUniqueId,
            legalEntity: params.legalEntity,
            type: routerState?.type,
            isFromMap: routerState?.isFromMap,
            formValues,
            uniqueIds: routerState?.uniqueIds,
            options: routerState?.options,
          },
        });
      }
      navigate(`${ERouteLinks.Firearms}${routerState?.query || ''}`, {
        state: {
          legalityUniqueId: routerState?.legalityUniqueId,
          legalEntity: params.legalEntity,
          type: routerState?.type,
          isFromMap: routerState?.isFromMap,
          formValues,
          uniqueIds: routerState?.uniqueIds,
          options: routerState?.options,
        },
      });
    };
  } else if (routerState?.context === ESelectLegalEntityContext.TRANSACTIONS) {
    handleSelect = (params: any) =>
      navigate(
        routerState.groupId
          ? `${ERouteLinks.LegalEntityAddTransactionForGroup.replace(
            ':id',
            routerState?.id,
          ).replace(':groupId', routerState?.groupId)}?type=${routerState?.type
          }&expand=groups${routerState?.query || ''}`
          : `${ERouteLinks.MiddleTransaction.replace(':id', routerState?.id)}${routerState?.query || ''
          }`,
        {
          state: {
            ...routerState,
            legalEntityFullName: params.legalEntity?.firstName
              ? `${params.legalEntity?.firstName} ${params.legalEntity?.lastName}`
              : `${params.legalEntity?.organizationName}`,
            legalEntityId: params.legalEntity?.id,
            type: params?.firstName
              ? ELegalEntityTypes.individual
              : ELegalEntityTypes.organisation,
          },
        },
      );
  } else if (routerState?.context === ESelectLegalEntityContext.ANCILLARY_TRANSACTIONS) {
    handleSelect = (params: any) =>
      navigate(
        `${ERouteLinks.AncillariesMiddleTransaction.replace(':id', routerState?.id)}${routerState?.query || ''
        }`,
        {
          state: {
            ...routerState,
            legalEntityFullName: params.legalEntity?.firstName
              ? `${params.legalEntity?.firstName} ${params.legalEntity?.lastName}`
              : `${params.legalEntity?.organizationName}`,
            legalEntityId: params.legalEntity?.id,
            type: params?.firstName
              ? ELegalEntityTypes.individual
              : ELegalEntityTypes.organisation,
          },
        },
      );
  } else if (routerState?.context === ESelectLegalEntityContext.ANCILLARY_GROUP_TRANSACTIONS) {
    handleSelect = (params: any) =>
      navigate(
        routerState.groupId
          ? `${ERouteLinks.LegalEntityAddTransactionForAncillaryGroup.replace(
            ':id',
            routerState?.id,
          ).replace(':groupId', routerState?.groupId)}?type=${routerState?.type
          }&expand=ancillaryGroups${routerState?.query || ''}`
          : `${ERouteLinks.MiddleTransaction.replace(':id', routerState?.id)}${routerState?.query || ''
          }`,
        {
          state: {
            ...routerState,
            legalEntityFullName: params.legalEntity?.firstName
              ? `${params.legalEntity?.firstName} ${params.legalEntity?.lastName}`
              : `${params.legalEntity?.organizationName}`,
            legalEntityId: params.legalEntity?.id,
            type: params?.firstName
              ? ELegalEntityTypes.individual
              : ELegalEntityTypes.organisation,
          },
        },
      );
  } else if (routerState?.context === ESelectLegalEntityContext.SEARCH_TRANSACTIONS) {
    handleSelect = (params: any) => {
      navigate(`${ERouteLinks.Transactions}${routerState?.query || ''}`, {
        state: {
          ...routerState,
          legalEntity: params.legalEntity,
        },
      });
    };
  } else if (routerState?.context === ESelectLegalEntityContext.SEARCH_ANCILLARIES) {
    handleSelect = (params: any) => {
      let formValues = routerState?.form;
      if (params.type === ESelectLE.keeper) {
        formValues = {
          ...formValues,
          uniqueIds: routerState?.uniqueIds,
          accessoriesUniqueIds: routerState?.accessoriesUniqueIds,
          ammunitionUniqueIds: routerState?.ammunitionUniqueIds,
          componentsUniqueIds: routerState?.componentsUniqueIds,
          keeperId: params.legalEntity.id,
          keeperName: undefined,
        };
      } else if (params.type === ESelectLE.owner) {
        formValues = {
          ...formValues,
          uniqueIds: routerState?.uniqueIds,
          accessoriesUniqueIds: routerState?.accessoriesUniqueIds,
          ammunitionUniqueIds: routerState?.ammunitionUniqueIds,
          componentsUniqueIds: routerState?.componentsUniqueIds,
          ownerId: params.legalEntity.id,
          ownerName: undefined,
        };
      }
      if (routerState.returnPath) {
        navigate(routerState.returnPath, {
          state: {
            legalityUniqueId: routerState?.legalityUniqueId,
            legalEntity: params.legalEntity,
            type: routerState?.type,
            module: routerState?.module,
            formValues,
            uniqueIds: routerState?.uniqueIds,
            accessoriesUniqueIds: routerState?.accessoriesUniqueIds,
            ammunitionUniqueIds: routerState?.ammunitionUniqueIds,
            componentsUniqueIds: routerState?.componentsUniqueIds,
            options: routerState?.options,
            ancillaryOption: routerState?.ancillaryOption,
            selectedAncillaries: routerState?.selectedAncillaries,
          },
        });
      } else {
        navigate(`${ERouteLinks.Ancillaries}${routerState?.query || ''}`, {
          state: {
            legalityUniqueId: routerState?.legalityUniqueId,
            legalEntity: params.legalEntity,
            type: routerState?.type,
            module: routerState?.module,
            formValues,
            uniqueIds: routerState?.uniqueIds,
            accessoriesUniqueIds: routerState?.accessoriesUniqueIds,
            ammunitionUniqueIds: routerState?.ammunitionUniqueIds,
            componentsUniqueIds: routerState?.componentsUniqueIds,
            options: routerState?.options,
          },
        });
      }
    };
  } else if (routerState?.context === ESelectLegalEntityContext.ASSOCIATED_INDIVIDUAL) {
    if (routerState?.returnPath) {
      handleSelect = (params: any) => {
        navigate(routerState.returnPath, {
          state: {
            form: {
              ...routerState?.form,
              associatedIndividualUniqueId: params.legalEntity.id,
              name: `${params.legalEntity?.firstName} ${params.legalEntity?.lastName}`,
              associatedIndividualName: `${params.legalEntity?.firstName} ${params.legalEntity?.lastName}`,
            },
          },
        });
      };
    }
  } else if (routerState?.context === ESelectLegalEntityContext.SEARCH_LICENCES) {
    if (routerState?.returnPath) {
      handleSelect = (params: any) => {
        navigate(routerState.returnPath, {
          state: {
            form: {
              ...routerState?.form,
              legalEntityUniqueId: params.legalEntity.id,
              legalEntity: `${params.legalEntity?.firstName} ${params.legalEntity?.lastName}`,
            },
          },
        });
      };
    }
  }

  useEffect(() => {
    if (routerState) {
      const breadcrumb = routerState?.breadcrumbs;
      if (
        routerState.returnLink &&
        breadcrumb.length > 1 &&
        typeof breadcrumb[breadcrumb.length - 2] === 'string'
      ) {
        breadcrumb[breadcrumb.length - 2] = {
          label: breadcrumb[breadcrumb.length - 2],
          route: routerState.returnLink,
          state: routerState,
        };
      }
      setNewBreadcrumbs(breadcrumb);
    }
  }, [routerState]);

  return (
    <>
      <Breadcrumbs items={newBreadcrumbs} />
      <StyledPageTitle variant="h4">{t('legals.search.selectLegal.label')}</StyledPageTitle>
      {routerState && (
        <LEAccordions
          type={ELEAccordionsPageTypes.SelectLegalEntity}
          handleSelect={(params: any) => {
            handleSelect(params);
          }}
          saveRouterStateAfterClearAll={true}
        />
      )}
    </>
  );
};

export { SelectLegalEntityPage };
